import React, { useState, useEffect } from 'react';
import { Check, ChevronLeft, ChevronRight, ArrowUpDown, UnfoldHorizontal, Columns2, X, BoxSelect } from 'lucide-react';
import { ReactComponent as BodyweightIcon } from '../../../assets/icons/bodyweight.svg';
import { ReactComponent as DumbbellIcon } from '../../../assets/icons/dumbbell.svg';
import { ReactComponent as BarbellIcon } from '../../../assets/icons/barbell.svg';
import { ReactComponent as MachineIcon } from '../../../assets/icons/machine.svg';
import { ReactComponent as CableIcon } from '../../../assets/icons/cable.svg';
import { ReactComponent as KettlebellIcon } from '../../../assets/icons/kettlebell.svg';
import { ReactComponent as BandIcon } from '../../../assets/icons/bands.svg';

const StepFive = ({ formData, updateFields }) => {
  const splitOptions = [
    { label: 'Push / Pull / Legs', icon: <UnfoldHorizontal className="w-9 h-8 text-white" /> },
    { label: 'Upper Lower', icon: <ArrowUpDown className="w-9 h-8 text-white" /> },
    { label: '2 body parts per day', icon: <Columns2 className="w-9 h-8 text-white" /> },
    { label: 'Full Body', icon: <BodyweightIcon className="w-9 h-8 text-white" /> }
  ];

  const splits = [
    ...splitOptions,
    { label: 'Select for me', icon: <BoxSelect className="w-9 h-8 text-white" /> }
  ];

  const getUpdatedSplit = (selectedSplit) => {
    if (selectedSplit === 'Select for me') {
      const availableSplits = splitOptions.map(split => split.label).join(', ');
      return `Please select the optimal split from these options: ${availableSplits}`;
    }
    return selectedSplit;
  };

  // Set initial split if not already set
  useEffect(() => {
    if (!formData.split) {
      updateFields({ split: getUpdatedSplit(splits[0].label) });
    }
  }, []);

  const [currentIndex, setCurrentIndex] = useState(
    formData.split ? splits.findIndex(split => split.label === formData.split) : 0
  );
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNext = () => {
    if (isAnimating || currentIndex === splits.length - 1) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex + 1);
    updateFields({ split: getUpdatedSplit(splits[currentIndex + 1].label) });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handlePrev = () => {
    if (isAnimating || currentIndex === 0) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex - 1);
    updateFields({ split: getUpdatedSplit(splits[currentIndex - 1].label) });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleCardClick = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    updateFields({ split: getUpdatedSplit(splits[index].label) });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const equipmentList = [
    { name: "Body weight", icon: BodyweightIcon },
    { name: "Dumbbell", icon: DumbbellIcon },
    { name: "Barbell", icon: BarbellIcon },
    { name: "Machine", icon: MachineIcon },
    { name: "Cable", icon: CableIcon },
    { name: "Kettlebell", icon: KettlebellIcon },
    { name: "Band", icon: BandIcon },
  ];

  const handleEquipmentChange = (equipment) => {
    const currentEquipment = formData.equipment || [];
    
    if (equipment === 'All Equipment') {
      const allEquipmentNames = equipmentList.map(e => e.name);
      const hasAllEquipment = allEquipmentNames.every(name => currentEquipment.includes(name));
      
      updateFields({ 
        equipment: hasAllEquipment ? [] : allEquipmentNames 
      });
      return;
    }

    const newEquipment = currentEquipment.includes(equipment)
      ? currentEquipment.filter(e => e !== equipment)
      : [...currentEquipment, equipment];
    updateFields({ equipment: newEquipment });
  };

  const isAllSelected = equipmentList.every(({ name }) => 
    (formData.equipment || []).includes(name)
  );

  return (
    <div className="space-y-8 w-full">
      {/* Workout Split Section */}
      <div className="space-y-4">
        <label className="block text-lg aw-label">Choose your workout split</label>
        <div className="relative h-40 rounded-2xl px-2"
          style={{
            background: 'linear-gradient(180deg, #f7f7f9 0%, #ececef 100%)',
            border: '1px solid #d8d8dc',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.7)'
          }}
        >
          {/* Edge fades */}
          <div className="absolute left-0 top-0 h-full w-20 rounded-l-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to right, #f7f7f9, rgba(247,247,249,0))' }} />
          <div className="absolute right-0 top-0 h-full w-20 rounded-r-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to left, #ececef, rgba(236,236,239,0))' }} />

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`aw-round-btn absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === 0 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={handleNext}
            disabled={currentIndex === splits.length - 1}
            className={`aw-round-btn absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === splits.length - 1 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Carousel */}
          <div className="relative w-full h-full overflow-hidden px-14">
            <div className="w-full h-full flex items-center justify-center">
              {splits.map((split, index) => {
                const position = index - currentIndex;
                const selected = position === 0;
                return (
                  <div
                    key={split.label}
                    onClick={() => handleCardClick(index)}
                    className={`absolute w-40 transition-all duration-300 ease-in-out
                      ${Math.abs(position) <= 1 ? 'cursor-pointer' : ''}`}
                    style={{
                      transform: `translateX(${position * 95}%) scale(${selected ? 1 : 0.8})`,
                      opacity: Math.abs(position) > 1 ? 0 : 1 - Math.abs(position) * 0.5,
                      zIndex: 10 - Math.abs(position),
                      pointerEvents: Math.abs(position) <= 1 ? 'auto' : 'none'
                    }}
                  >
                    <div className={`aw-card-option ${selected ? 'aw-card-option-selected' : 'aw-card-option-dark'}`}>
                      <div className="flex flex-col items-center">
                        {split.icon}
                        <span className="text-center font-semibold mt-2 text-sm">{split.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Equipment Section */}
      <div>
        <h2 className="text-lg aw-label mb-2">
          Equipment Preference
        </h2>
        <p className="text-sm text-gray-500 mb-2">
          Select all equipment you have access to. Your workout plan will be customized based on your equipment preferences.
        </p>
        <p className={`text-sm mb-6 ${
          (formData.equipment || []).length === 0 ? 'text-gray-500' :
          (formData.equipment || []).length <= 4 ? 'text-green-600' : 'text-red-600'
        }`}>
          Recommended: Choose 1-4 selection of equipment for optimal workout result
          {(formData.equipment || []).length > 4 && ' (Not recommended)'}
        </p>
        <div className="grid grid-cols-3 gap-3">
          {equipmentList.map(({ name, icon: Icon }) => {
            const selected = (formData.equipment || []).includes(name);
            return (
              <button
                type="button"
                key={name}
                onClick={() => handleEquipmentChange(name)}
                className={`aw-card-option flex flex-col items-center justify-center
                  ${selected ? 'aw-card-option-selected' : ''}`}
              >
                <div className="mb-2">
                  <Icon className={`w-12 h-12 ${selected ? 'text-white' : ''}`} />
                </div>
                <span className="text-center text-sm font-semibold">{name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl p-4"
        style={{
          background: 'linear-gradient(180deg, #fff5f5 0%, #ffe8e8 100%)',
          border: '1px solid #f5b8b8',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 8px rgba(154,3,3,0.08)'
        }}
      >
        <p className="text-sm text-red-700 font-medium">
          💡 Tip: Having access to more equipment types will give you a more varied workout plan, but you can get a great workout with just bodyweight exercises too!
        </p>
      </div>
    </div>
  );
};

export default StepFive;